/**
 * VH-126: the review gate.
 *
 * Candidates with a photo, a confidence and whether the machine is already
 * here. The user deselects, renames, and only then confirms — nothing is
 * written to equipment until the Confirm button is pressed, which the backend
 * enforces independently.
 *
 * Low-confidence candidates are shown and flagged, never hidden: the machines
 * the model is least sure about are exactly the ones the person standing in
 * the room can settle.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

export default function ScanReview({ candidates = [], onConfirm, onDiscard, isSaving = false }) {
  const { t } = useTranslation()

  const [accepted, setAccepted] = useState(() => new Set(candidates.map((c) => c.id)))
  const [names, setNames] = useState({})

  const toggle = (id) => {
    setAccepted((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const rename = (id, value) => setNames((previous) => ({ ...previous, [id]: value }))

  const handleConfirm = () => {
    const corrections = {}
    Object.entries(names).forEach(([id, value]) => {
      const trimmed = value.trim()
      const original = candidates.find((c) => String(c.id) === String(id))
      if (trimmed && original && trimmed !== original.proposed_name_en) {
        corrections[id] = trimmed
      }
    })
    onConfirm({ acceptedIds: Array.from(accepted), nameCorrections: corrections })
  }

  if (candidates.length === 0) {
    return (
      <div className="text-center py-10" data-testid="scan-review-empty">
        <p className="text-gray-600">{t('trainingLocations.scan.nothingFound')}</p>
      </div>
    )
  }

  return (
    <div data-testid="scan-review">
      <ul className="space-y-3">
        {candidates.map((candidate) => {
          const isAccepted = accepted.has(candidate.id)
          return (
            <li
              key={candidate.id}
              className={`flex items-start gap-3 rounded-lg border p-3 ${
                isAccepted ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-surface-container'
              }`}
              data-testid={`candidate-${candidate.id}`}
            >
              <input
                type="checkbox"
                checked={isAccepted}
                onChange={() => toggle(candidate.id)}
                className="mt-2 h-5 w-5"
                aria-label={t('trainingLocations.scan.acceptAria', {
                  name: candidate.proposed_name_en,
                })}
              />

              {candidate.crop_image_url ? (
                <img
                  src={candidate.crop_image_url}
                  alt={candidate.proposed_name_en}
                  className="h-20 w-20 rounded object-cover"
                />
              ) : (
                // Equipment from before this feature has no photo. A neutral
                // placeholder, never a broken image.
                <div className="h-20 w-20 rounded bg-gray-100" aria-hidden="true" />
              )}

              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={names[candidate.id] ?? candidate.proposed_name_en}
                  onChange={(e) => rename(candidate.id, e.target.value)}
                  className="input w-full"
                  aria-label={t('trainingLocations.scan.nameAria')}
                />
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                  {candidate.matched_equipment_id ? (
                    <span
                      className="inline-flex items-center gap-1 text-green-700"
                      data-testid={`already-here-${candidate.id}`}
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      {t('trainingLocations.scan.alreadyHere')}
                    </span>
                  ) : (
                    <span className="text-gray-500">{t('trainingLocations.scan.new')}</span>
                  )}
                  {candidate.is_low_confidence && (
                    <span
                      className="inline-flex items-center gap-1 text-amber-700"
                      data-testid={`low-confidence-${candidate.id}`}
                    >
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      {t('trainingLocations.scan.lowConfidence')}
                    </span>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <div className="mt-5 flex gap-3">
        <button
          type="button"
          className="btn-primary"
          onClick={handleConfirm}
          disabled={isSaving}
          data-testid="confirm-scan"
        >
          {t('trainingLocations.scan.confirm', { count: accepted.size })}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={onDiscard}
          disabled={isSaving}
          data-testid="discard-scan"
        >
          {t('trainingLocations.scan.discard')}
        </button>
      </div>
    </div>
  )
}
